import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments, FilterList, ZeroState } from '@modernloop/shared/components';
import { SearchIcon } from '@modernloop/shared/icons';
import { AutocompleteChangeDetails, AutocompleteChangeReason, ListItem, ListSubheader } from '@mui/material';
import { set } from 'lodash';

import { EmployeeFilterFragment, TrainingStatus } from 'src/generated/mloop-graphql';

import {
  doesModuleMemberNeedsApproval,
  isModuleMemberDisabledForScheduling,
  isModuleMemberTrained,
} from 'src/entities/InterviewModuleMember/utils';
import InterviewModuleMember from 'src/entities/InterviewPlan/InterviewModuleMember';

import { JobStageInterviewSeat } from 'src/store/slices/job-stage-interview-seat';

const LIST_ITEM_HEIGHT = 40;
const LIST_SUBHEADER_HEIGHT = 24;

enum DisplayMemberStatus {
  Trained = 'Trained',
  Trainees = 'Trainees',
}

type Fragments = {
  interviewModuleMembers: EmployeeFilterFragment[];
};

type Props = {
  seat: JobStageInterviewSeat;
  onUpdated: (seat: JobStageInterviewSeat) => void;
};

const EmployeeFilter: FCWithFragments<Fragments, Props> = ({ interviewModuleMembers, seat, onUpdated }) => {
  const { t } = useTranslation();

  const getSelectedEmployees = () => {
    return interviewModuleMembers.filter((interviewer) => {
      if (interviewer.status === TrainingStatus.Trained) {
        return seat.selectedTrainedInterviewerIds?.includes(interviewer.employeeId);
      }
      if (interviewer.status === TrainingStatus.Shadow) {
        return seat.selectedShadowInterviewerIds?.includes(interviewer.employeeId);
      }
      if (interviewer.status === TrainingStatus.ReverseShadow) {
        return seat.selectedReverseShadowInterviewerIds?.includes(interviewer.employeeId);
      }
      return false;
    });
  };

  const handleMemberChange = (
    _props,
    _values,
    reason: AutocompleteChangeReason,
    details: AutocompleteChangeDetails<EmployeeFilterFragment>
  ) => {
    const isMemberAdded = reason === 'selectOption';
    const changedMember = details.option;

    const updatedMemberIds = {
      selectedTrainedInterviewerIds: seat.selectedTrainedInterviewerIds || [],
      selectedShadowInterviewerIds: seat.selectedShadowInterviewerIds || [],
      selectedReverseShadowInterviewerIds: seat.selectedReverseShadowInterviewerIds || [],
    };

    // if member was added, add the member to the selected list
    if (isMemberAdded) {
      if (changedMember.status === TrainingStatus.Trained) {
        set(updatedMemberIds, 'selectedTrainedInterviewerIds', [
          ...(updatedMemberIds.selectedTrainedInterviewerIds || []),
          changedMember.employeeId,
        ]);
      } else if (changedMember.status === TrainingStatus.Shadow) {
        set(updatedMemberIds, 'selectedShadowInterviewerIds', [
          ...(updatedMemberIds.selectedShadowInterviewerIds || []),
          changedMember.employeeId,
        ]);
      } else if (changedMember.status === TrainingStatus.ReverseShadow) {
        set(updatedMemberIds, 'selectedReverseShadowInterviewerIds', [
          ...(updatedMemberIds.selectedReverseShadowInterviewerIds || []),
          changedMember.employeeId,
        ]);
      }
    } else if (!isMemberAdded) {
      // if member was removed, remove the member from the selected list
      if (changedMember.status === TrainingStatus.Trained) {
        set(
          updatedMemberIds,
          'selectedTrainedInterviewerIds',
          updatedMemberIds.selectedTrainedInterviewerIds?.filter((id) => id !== changedMember.employeeId)
        );
      } else if (changedMember.status === TrainingStatus.Shadow) {
        set(
          updatedMemberIds,
          'selectedShadowInterviewerIds',
          updatedMemberIds.selectedShadowInterviewerIds?.filter((id) => id !== changedMember.employeeId)
        );
      } else if (changedMember.status === TrainingStatus.ReverseShadow) {
        set(
          updatedMemberIds,
          'selectedReverseShadowInterviewerIds',
          updatedMemberIds.selectedReverseShadowInterviewerIds?.filter((id) => id !== changedMember.employeeId)
        );
      }
    }

    onUpdated({
      ...seat,
      ...updatedMemberIds,
    });
  };

  const groupBy = (option: EmployeeFilterFragment) => {
    return isModuleMemberTrained(option.status) ? DisplayMemberStatus.Trained : DisplayMemberStatus.Trainees;
  };

  return (
    <FilterList
      hideSearch
      options={interviewModuleMembers}
      values={getSelectedEmployees()}
      getOptionLabel={(option) => option.employee?.fullName ?? ''}
      getChildSize={(child: React.ReactNode) => {
        let HEIGHT = LIST_ITEM_HEIGHT;
        /* *  TODO Ticket - ENG-13741  * */
        // Subheader component is not an option
        if ((child as React.ReactElement).props.role !== 'option') {
          return LIST_SUBHEADER_HEIGHT;
        }

        const { interviewModuleMember } = (child as React.ReactElement).props.children.props;
        if (interviewModuleMember.employee.title) {
          HEIGHT += 18;
        }
        if (!isModuleMemberTrained(interviewModuleMember.status)) {
          if (
            doesModuleMemberNeedsApproval(
              interviewModuleMember.status,
              interviewModuleMember.stats?.currentTrainingSessionStatus
            )
          ) {
            return HEIGHT + 18;
          }
          return HEIGHT + 18;
        }
        return HEIGHT;
      }}
      getListHeight={() => 300}
      onChange={handleMemberChange}
      noOptionsText={
        <ZeroState label={t('hiring-dashboard-filter:filter.noInterviewersFound')} icon={<SearchIcon />} />
      }
      renderOption={(props, option, state) => (
        <ListItem {...props}>
          <InterviewModuleMember interviewModuleMember={option} selected={state.selected} />
        </ListItem>
      )}
      groupBy={groupBy}
      renderGroup={(params) => {
        return [
          <ListSubheader disableSticky key={params.key} component="div">
            {params.group === DisplayMemberStatus.Trained && t('hiring-dashboard-filter:filter.trained')}
            {params.group === DisplayMemberStatus.Trainees && t('hiring-dashboard-filter:filter.trainees')}
          </ListSubheader>,
          params.children,
        ];
      }}
      getOptionDisabled={(option) => isModuleMemberDisabledForScheduling({ moduleMember: option }, null)}
    />
  );
};

/**
 * Note: Before deleteing the unused keys from the fragments, make sure that the keys are not used in other components.
 */

EmployeeFilter.fragments = {
  interviewModuleMembers: gql`
    ${InterviewModuleMember.fragments.interviewModuleMember}
    ${isModuleMemberDisabledForScheduling.fragments.moduleMember}
    fragment EmployeeFilter on InterviewModuleMember {
      id
      ...isModuleMemberDisabledForScheduling_ModuleMember
      ...InterviewModuleMember_interviewModuleMember
      interviewModuleId
      employeeId
      status
      shadowsRequired
      reverseShadowsRequired
      pausedUntil
      stats {
        completedAsReverseShadow
        completedAsShadow
        shadowOffset
        reverseShadowOffset
      }
      employee {
        id
        fullName
        email
        title
        internalProfilePictureUrl
        attributes {
          id
          value
        }
        interviewPauseDates {
          start
          end
        }
      }
    }
  `,
};

export default EmployeeFilter;
